import { motion } from 'framer-motion';

const FEATURES = [
  {
    title: 'Encrypted local vault',
    body: 'Your vault is sealed with PBKDF2 and AES-GCM through the Web Crypto API and stored only on your device. Nothing decrypted ever leaves memory.',
    offset: false,
  },
  {
    title: 'One phrase, many accounts',
    body: 'Recover from a single mnemonic and organize funds across as many named accounts as you need, without juggling separate seeds.',
    offset: true,
  },
  {
    title: 'Locked by default',
    body: 'Every restart starts locked. An idle timeout re-locks the wallet automatically, so a forgotten tab is never an open door.',
    offset: false,
  },
  {
    title: 'Approvals on your terms',
    body: 'Every dApp connection and sensitive action asks first. Connected sites are listed in Settings and can be revoked in one click.',
    offset: true,
  },
  {
    title: 'Built for shielded transactions',
    body: 'Vela speaks the Midnight network natively, mainnet-first, with the wallet SDK integration handling sync and checkpoint recovery.',
    offset: false,
  },
  {
    title: 'Developer mode, opt-in',
    body: 'Testnets, debug surfaces, and diagnostics stay hidden until you switch on developer mode from Settings.',
    offset: true,
  },
];

export function Features() {
  return (
    <section id="features" className="relative border-t border-white/[0.05] bg-midnight-950 py-28">
      <div className="mx-auto max-w-6xl px-6">
        <SectionHeading
          eyebrow="What you get"
          title="Custody that stays out of your way."
          body="No accounts to create, no servers to trust. Everything that matters lives, encrypted, in your browser."
        />

        <div className="mt-16 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className={`group rounded-2xl border border-white/[0.06] bg-midnight-900/50 p-7 transition-colors hover:border-teal/30 hover:bg-midnight-900 ${
                f.offset ? 'sm:translate-y-6' : ''
              }`}
            >
              <FeatureGlyph index={i} />
              <h3 className="mt-5 text-xl">{f.title}</h3>
              <p className="mt-2.5 text-sm leading-relaxed text-mist">{f.body}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  body,
  align = 'left',
}: {
  eyebrow: string;
  title: string;
  body?: string;
  align?: 'left' | 'center';
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={align === 'center' ? 'mx-auto max-w-2xl text-center' : 'max-w-2xl'}
    >
      <span className="text-xs uppercase tracking-[0.16em] text-teal-soft">{eyebrow}</span>
      <h2 className="mt-3 text-balance text-3xl sm:text-4xl">{title}</h2>
      {body && <p className="mt-4 text-balance text-base leading-relaxed text-mist">{body}</p>}
    </motion.div>
  );
}

function FeatureGlyph({ index }: { index: number }) {
  const glyphs = [
    <path d="M12 3l7 3v5c0 4.5-3 7.5-7 9-4-1.5-7-4.5-7-9V6l7-3z" />,
    <path d="M12 3v18M6 8c0 3 6 3 6 6s6 3 6 6M6 20c0-3 6-3 6-6" />,
    <path d="M6 11V8a6 6 0 1112 0v3M5 11h14v9H5z" />,
    <path d="M9 12l2 2 4-4M12 3l8 4v5c0 4.5-3.4 7.7-8 9-4.6-1.3-8-4.5-8-9V7l8-4z" />,
    <path d="M4 14l4-6 4 3 4-8 4 11M4 20h16" />,
    <path d="M4 17l6-6-2-2 6-6M8 17h12M12 21h4" />,
  ];
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="text-teal-soft transition-transform duration-300 group-hover:scale-110"
      aria-hidden="true"
    >
      {glyphs[index % glyphs.length]}
    </svg>
  );
}
